import dayjs from "dayjs";
import { createPath } from "react-router-dom";

export const GOOGLE_CONNECT_STATES = {
  OFF: "off",
  ON: "on",
  BROKEN: "broken",
  INACTIVE: "inactive",
};

/**
 * Google Connect channel the user is on, as served by the `googleConnectState` virtual.
 * @param {Object} user
 * @returns {"off"|"on"|"broken"|"inactive"}
 */
export const googleConnectState = (user) =>
  user?.googleConnectState ?? GOOGLE_CONNECT_STATES.OFF;

/**
 * Whether the user asked to turn sync off and the removal from Google has not finished.
 * No connect path is offered until it has.
 * @param {Object} user
 * @returns {boolean}
 */
export const isGoogleDisconnectPending = (user) =>
  Boolean(user?.hasPendingGoogleDisconnect);

/**
 * Whether the Google Connect surfaces apply to this user: they signed in with Google,
 * or they already hold a grant. Every surface asks this, so a grant holder on another
 * provider still gets somewhere to resolve the state they are being shown.
 * @param {Object} user
 * @returns {boolean}
 */
export const showsGoogleConnect = (user) =>
  user?.provider === "google" ||
  isGoogleDisconnectPending(user) ||
  googleConnectState(user) !== GOOGLE_CONNECT_STATES.OFF;

/**
 * Whether the user's calendars have stopped updating: a dead grant (broken) or a lapsed
 * membership (inactive). Both read the same to the user, so every surface styles them alike.
 * @param {Object} user
 * @returns {boolean}
 */
export const isGoogleConnectStopped = (user) => {
  const state = googleConnectState(user);
  return (
    state === GOOGLE_CONNECT_STATES.BROKEN ||
    state === GOOGLE_CONNECT_STATES.INACTIVE
  );
};

/**
 * Router state for opening the turn-off page, so its exits come back to `location`.
 * @param {{ pathname: string, search?: string, hash?: string }} location
 * @returns {{ returnTo: string }}
 */
export const turnOffGoogleConnectState = (location) => ({
  returnTo: createPath(location),
});

/**
 * Sub-line under the side-menu Google Connect row.
 * @param {string} state
 * @param {{ expiredAt?: string|Date, lastUpdated?: string|Date, disconnectPending?: boolean }} [options]
 * @returns {string}
 */
export const googleConnectHealthLine = (
  state,
  { expiredAt, lastUpdated, disconnectPending } = {}
) => {
  if (disconnectPending)
    return "Removing your calendars from Google Calendar · this takes a few minutes";
  if (state === GOOGLE_CONNECT_STATES.ON) {
    return lastUpdated
      ? `Healthy · last updated ${dayjs(lastUpdated).format("MMM D")}`
      : "Healthy";
  }
  if (state === GOOGLE_CONNECT_STATES.OFF)
    return "Keep your calendars up to date automatically";
  if (state === GOOGLE_CONNECT_STATES.INACTIVE) return "Your membership has ended";
  return expiredAt
    ? `Not updating since ${dayjs(expiredAt).format("MMM D")}`
    : "Not updating";
};
